import { useEffect, useState, useRef } from "react";
import QRCode from "qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { useToast } from "@/hooks/useToast";

interface QRCodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  url: string;
  title?: string;
}

export function QRCodeDialog({ open, onOpenChange, url, title = "Scan to open profile" }: QRCodeDialogProps) {
  const [qrDataUrl, setQrDataUrl] = useState<string>("");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (open && url) {
      // Generate QR code for display
      QRCode.toDataURL(url, {
        width: 280,
        margin: 2,
        color: {
          dark: "#000000",
          light: "#ffffff",
        },
        errorCorrectionLevel: "M",
      }).then(setQrDataUrl).catch(console.error);

      // Generate high-res QR for download
      if (canvasRef.current) {
        QRCode.toCanvas(canvasRef.current, url, {
          width: 1024,
          margin: 4,
          color: {
            dark: "#000000",
            light: "#ffffff",
          },
          errorCorrectionLevel: "H",
        }).catch(console.error);
      }
    }
  }, [open, url]);

  const handleDownload = () => {
    if (!canvasRef.current) return;

    try {
      const link = document.createElement("a");
      link.download = "zapmeacoffee-qr.png";
      link.href = canvasRef.current.toDataURL("image/png");
      link.click();
      toast({ title: "QR code downloaded! 📥" });
    } catch {
      toast({
        title: "Download failed",
        description: "Could not download QR code",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm rounded-3xl border-0 shadow-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-center">{title}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-6 py-4">
          {/* QR Code Display */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-primary/5 rounded-3xl blur-2xl" />
            <div className="relative bg-white p-4 rounded-2xl shadow-lg">
              {qrDataUrl ? (
                <img 
                  src={qrDataUrl} 
                  alt="QR Code" 
                  className="w-56 h-56 rounded-lg"
                />
              ) : (
                <div className="w-56 h-56 bg-secondary/50 rounded-lg animate-pulse" />
              )}
            </div>
          </div>

          {/* URL Preview */}
          <p className="text-xs text-muted-foreground text-center break-all px-4 max-w-full">
            {url}
          </p>

          {/* Download Button */}
          <Button
            onClick={handleDownload}
            className="w-full h-12 rounded-2xl bg-primary hover:bg-primary/90 shadow-lg shadow-primary/25"
          >
            <Download className="h-4 w-4 mr-2" />
            Download QR Code
          </Button>
        </div>

        {/* Hidden canvas for high-res download */}
        <canvas ref={canvasRef} className="hidden" />
      </DialogContent>
    </Dialog>
  );
}
